import React from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Container from '@material-ui/core/Container';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '../Button';
import MatchIcon from '../../icons/MatchIcon';
import SearchIcon from '../../icons/SearchIcon';
import RatingIcon from '../../icons/RatingIcon';

const quickSearchButtons = [
  'Plan Management',
  'Nursing',
  'Transport',
  'Support Work',
  'Occupational Therapy',
  'Meals',
  'Gardening',
  'Cleaning',
  'Physiotherapy'
];

const useStyles = makeStyles(theme => ({
  clickAway: {
    backgroundColor: 'transparent',
    border: 'none',
    height: 'calc(100vh - 100px)',
    width: '100vw',
    position: 'absolute',
    zIndex: 998,
  },
  root: {
    width: '100vw',
    position: 'absolute',
    borderTop: `1px solid ${theme.palette.common.battleshipGrey}`,
    borderBottom: `1px solid ${theme.palette.common.battleshipGrey}`,
    backgroundColor: theme.palette.common.white,
    left: 0,
    zIndex: 999,
  },
  menu: {
    margin: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    padding: '2rem 1.5rem 0 1.5rem',
    background: 'transparent',
    '&.MuiGrid-item': {
      padding: 0,
    },
  },
  item: {
    '&.MuiGrid-item': {
      padding: '0 1rem',
      margin: 0,
    },
  },
  section: {
    padding: '1rem 0',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'flex-start',
  },
  icon: {
    fontSize: '2.5rem',
    marginRight: '1rem',
  },
  text: {
    width: '100%',
    marginBottom: '1.5rem',
  },
  service: {
    width: '100%',
    marginBottom: '1.5rem',
  },
  linkButton: {
    padding: 0,
    fontWeight: 'normal',
    marginBottom: '1.5rem',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  headerButton: {
    padding: 0,
    margin: 0,
    marginTop: '-1.5rem',
    fontWeight: 500,
    fontSize: '1rem',
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
  quickSearchtitle: {
    fontWeight: 500,
    fontSize: '1rem',
    width: '100%',
    padding: '1rem 0',
    marginBottom: '0.5rem',
  },
  quickSearchButton: {
    padding: '0.5rem',
    marginRight: '1rem',
    marginBottom: '1rem',
    fontWeight: 400,
  },
  categoriesLink: {
    padding: 0,
    marginTop: '0.5rem',
    fontWeight: 400,
    '&:hover': {
      backgroundColor: 'transparent',
      textDecoration: 'underline',
    },
  },
}));

const ServicesMenu = ({closeServicesMenu}) => {
  let history = useHistory();
  const classes = useStyles();

  const handleServiceClick = (value) => {
    if (value !== '') {
      const query = new URLSearchParams({service: value}).toString();
      history.push(`/search?${query}`);
      closeServicesMenu();
    }
  };

  const closeOnEscape = (event) => {
    if (event.keyCode === 27) {
      closeServicesMenu();
    }
  };

  return (
    <React.Fragment>
      <div className={classes.clickAway} onClick={closeServicesMenu}/>
      <div className={classes.root}>
        <Container maxWidth="lg" fixed disableGutters={true}>
          <div className={classes.menu} onKeyUp={closeOnEscape}>
            <Grid container spacing={6}>
              <Grid item md={7} className={classes.item}>
                <div className={classes.section}>
                  <div className={classes.service}>
                    <MatchIcon className={classes.icon}/>
                    <Button
                      label="Service matching"
                      to="/requestmatch"
                      variant="text"
                      pointer="right"
                      className={classes.headerButton}
                      disableRipple
                    />
                    <Typography variant="body2" color="primary" className={classes.text}>
                      Quickly find services that best meet your unique needs.
                    </Typography>
                  </div>
                  <div className={classes.service}>
                    <SearchIcon className={classes.icon}/>
                    <Button
                      label="Provider search"
                      to="/search"
                      variant="text"
                      pointer="right"
                      className={classes.headerButton}
                      disableRipple
                    />
                    <Typography variant="body2" color="primary" className={classes.text}>
                      Search our directory of thousands of accredited providers.
                    </Typography>
                  </div>
                  <div className={classes.service}>
                    <RatingIcon className={classes.icon}/>
                    <Button
                      label="Ratings and reviews"
                      to="/review"
                      variant="text"
                      pointer="right"
                      className={classes.headerButton}
                      disableRipple
                    />
                    <Typography variant="body2" color="primary" className={classes.text}>
                      View user reviews and ratings of NDIS providers and services.
                    </Typography>
                  </div>
                </div>
              </Grid>
              <Grid item md={5} className={classes.item}>
                <div className={classes.section}>
                  <Typography variant="subtitle2" color="primary" className={classes.quickSearchtitle}>
                    Quick search for popular service categories:
                  </Typography>
                  {quickSearchButtons.map(label => (
                    <Button
                      key={label}
                      label={label}
                      onClick={()=>handleServiceClick(label)}
                      variant="outlined"
                      pointer="right"
                      className={classes.quickSearchButton}
                      disableRipple
                    />
                  ))}
                  <Button
                    label="See all service categories"
                    to="/servicecategories"
                    variant="text"
                    pointer="right"
                    className={classes.categoriesLink}
                    disableRipple
                  />
                </div>
              </Grid>
            </Grid>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

ServicesMenu.propTypes = {
  closeServicesMenu: PropTypes.func.isRequired,
};

export default ServicesMenu;
